/**
 * Core comment rendering and composer system: `<Comment>`, `<BasicComments>`,
 * `<CommentsInput>`, and the `CommentConfig` extension type.
 *
 * `CommentConfig` is the primary extension surface for comment features: it
 * declares widget slots (`commentTopWidgets`, `commentBelowWidgets`, etc.),
 * filter/ranker/booster callbacks, ranker markers (key/label pairs for the
 * sort UI), default-field providers for new comments
 * (`commentDefaultFieldProviders`), and post/delete triggers. Features add
 * behaviour by pushing into these arrays via `useConfig<CommentConfig>()`.
 *
 * Moderation is built in rather than contributed: a space's moderation mode
 * is a field of the space resource, so the composer takes its banners, its
 * post button wording, and its human-review modal from
 * `structure/question/moderation/` directly.
 *
 * Pure truncation helpers live in `comment.helpers.tsx` so they can be
 * unit-tested without rendering this heavier tree.
 */

import { Add, Edit, Reply, Subtract } from '@carbon/icons-react';
import type { ModerationRule } from '@repo/shared/community-guidelines';
import type { MultiLanguageString } from '@repo/shared/orpc/multi-language';
import { originalTextOf } from '@repo/shared/orpc/multi-language';
import { type Comment, commentPropertyKeys, type JsonValue } from '@repo/shared/orpc/procedures/comments';
import { perspectiveTagsToInput } from '@repo/shared/perspectives';
import { type ColorTokens, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import type { CommentTreeNode } from 'data/comment';
import { confirmCommentAsync, deleteCommentAsync, postOrUpdateCommentAsync, useCommentByKey, useCommentTree } from 'data/comment';
import { scoreCommentAsync } from 'data/comment-scoring';
import { useMyProfile } from 'data/me';
import { useIsReadOnly } from 'data/spaces';
import { getIsMobileWeb } from 'platform-specific/deviceinfo';
import { trackVisibility } from 'platform-specific/trackvisibility';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import {
    ModerationAdminBypassBanner,
    ModerationReportAndExplanation,
    useModerationPostActionLabel,
    useRejectionInterceptModal,
} from 'structure/question/moderation/mode-banners';
import { Catcher, CatchList } from 'system/catcher';
import { showApiErrorToast } from 'system/orpc-errors';
import { trackEvent } from 'util/analytics';
import { useDisplayText } from 'util/content-translation';
import type { Datastore } from 'util/datastore';
import { useDatastore, useIsInSidebar, usePersonaKey, useSessionData, useUserLoaded } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import { fireAndForget } from 'util/hooks';
import { useIsDesktopBreakpoint } from 'util/layout';
import { captureException } from 'util/sentry';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';
import { getFirstName, makeAssetUrl, toBool, useConstant } from 'util/util';

import { HorizBox, Pad, PadBox, Separator, VertBox } from './basics';
import { CTAButton, TextButton } from './button';
import { ComposerButton } from './button/composer-button';
import { CollapseBox } from './CollapseBox';
import { spacings } from './constants';
import { DeleteIcon } from './icon';
import { DeleteCommentModal } from './modal/comment/delete-comment';
import { DeleteVoteModal } from './modal/comment/delete-vote';
import { HumanModerationModal } from './modal/moderation/human-moderation-modal';
import { NavigationHeaderAction } from './navigation/navigation-header';
import { Byline } from './people';
import { CommentSkeleton } from './progress/comment-skeleton';
import { RichText } from './richtext';
import { Heading, TextArea, TextField, UtilityText } from './text';
import { ExpandableRichText } from './text/expandable-rich-text';
import { useTranslation } from './translation';
import type { ActionsMenuAction } from './ui/actions-menu';
import { ActionsMenu } from './ui/actions-menu';
import { SubtleButton } from './ui/subtle-button';

// A comment being composed or edited — a partial wire `Comment`. Server-set
// fields (`commentKey` minted on create, `authorKey`, `createdAt`) are absent
// while composing a new comment and present when editing, so an existing wire
// `Comment` is directly assignable to it, with no conversion — `text` keeps
// the same `MultiLanguageString` type `Comment.text` has (a plain string is
// already a valid value); `properties` is always present. Composing/editing
// always wants the original text, never a translation — read it with
// `originalTextOf(comment.text)`, never `comment.text` directly.
export type DraftComment = Omit<Partial<Comment>, 'text' | 'properties'> & {
    text: MultiLanguageString;
    properties: Record<string, JsonValue>;
};

export type CommentCallbackArgs = { datastore: Datastore; comment: Comment };

export type CommentWidget = React.ComponentType<{ comment: Comment }>;
// Stylers receive the active theme's colors from the invoking component (which
// reads useTheme()), so they can return silo-themed styles while staying pure.
export type CommentStylerArgs = CommentCallbackArgs & { colors: ColorTokens };
export type CommentStyler = (args: CommentStylerArgs) => Record<string, any> | null;
export type CommentConfig = {
    commentActions: React.ComponentType<{ comment: Comment; parent?: Comment; depth: number }>[];
    commentRightActions: React.ComponentType<{ comment: Comment; parent?: Comment; depth: number }>[];
    commentTopWidgets: CommentWidget[];
    commentAboveWidgets: CommentWidget[];
    commentBelowBylineWidgets: CommentWidget[];
    commentMiddleWidgets: CommentWidget[];
    commentBelowWidgets: CommentWidget[];
    commentBodyStylers: CommentStyler[];
    commentTruncationDisablers: ((args: CommentCallbackArgs) => boolean)[];
    commentStylers: CommentStyler[];
    commentAllowEmpty: boolean;
    commentEditTopWidgets: React.ComponentType<{
        comment: DraftComment;
        setComment: (comment: DraftComment) => void;
        onCancel?: () => void;
        onEditingDone?: () => void;
        loading?: boolean;
        rejection?: CommentRejection | null;
    }>[];
    commentPostBlockers: ((datastore: Datastore, comment: DraftComment) => boolean)[];
    commentDefaultFieldProviders: ((datastore: Datastore) => Record<string, unknown>)[];
    commentPostTriggers: ((args: CommentCallbackArgs) => Promise<void>)[];
    commentReplyPlaceholder: TLabel;
    commentInputPlaceholder: TLabel;
    commentYourReplyLabel?: TLabel;
    commentInputLoginAction: string;
    commentDeleteTriggers: ((args: CommentCallbackArgs) => Promise<void>)[];
    commentNewCommentSecondaryAction: ((args: { datastore: Datastore }) => (() => void) | null) | null;

    commentPageSize: number | null;

    composerPreview: React.ComponentType | null;
    composerSubtitle: (args: { datastore: Datastore; comment: DraftComment }) => TLabel | undefined;
    composerTopWidgets: React.ComponentType<{
        comment: DraftComment;
        setComment: (comment: DraftComment) => void;
        rejection?: CommentRejection | null;
    }>[];
    composerTopBanners: React.ComponentType<{ about?: string }>[];
    composerAboveBylineWidgets?: React.ComponentType[];

    replyAboveWidgets: React.ComponentType<{ comment: Comment }>[];
    replyBelowBylineWidgets: React.ComponentType<{ comment: Comment }>[];

    pageTopWidgets: React.ComponentType<{ comments: Comment[] }>[];
    pageTopLeftWidgets: React.ComponentType<{ comments: Comment[] }>[];
    pageTopRightWidgets: React.ComponentType<{ comments: Comment[] }>[];
    pageHiddenWidgets: React.ComponentType<{ comments: Comment[] }>[];
    pageBottomWidgets: React.ComponentType<{ comments: Comment[] }>[];
    betweenCommentsWidgets?: React.ComponentType<{}>[];
    pageShowEmptyHelp: boolean;
    noCommentsTitle: TLabel;
    noCommentsMessage: TLabel;

    topBanners: React.ComponentType[];

    postMenuActions?: ((args: {
        datastore: Datastore;
        comment: Comment;
        tree: CommentTreeNode[];
        readOnly: boolean;
    }) => ActionsMenuAction<{ comment: Comment }>[])[];
};

const DEFAULT_YOUR_REPLY_LABEL = t('comment.your_reply_label', 'Your reply');

export type CommentProps = { commentTree: CommentTreeNode };

export function Comment({ commentTree }: CommentProps) {
    const { comment, replies } = commentTree;
    const editing = useSessionData(['editComment', comment.commentKey]);
    const { commentAboveWidgets, commentBelowBylineWidgets, commentBelowWidgets, commentMiddleWidgets, commentStylers } =
        useConfig<CommentConfig>();
    const datastore = useDatastore();
    const { colors } = useTheme();
    const style = getCombinedStyle({ comment, stylers: commentStylers, datastore, colors });

    const commentRef = useRef(null);
    useEffect(() => {
        const stopTracking = trackVisibility(commentRef, () => {
            trackEvent(datastore, 'comment-long-view', { commentKey: comment.commentKey });
        });
        return stopTracking;
    }, [comment.commentKey, datastore]);

    return (
        <View testID='comment-thread'>
            {/* we use comment as testID and can still select a specific comment
             by its id attribute*/}
            <View id={comment.commentKey} ref={commentRef} style={[style, { paddingVertical: 20 }]} testID='comment'>
                <VertBox gap={20} wide>
                    <VertBox gap={8}>
                        <Catcher>
                            {commentAboveWidgets?.map((Widget, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Widget comment={comment} key={i} />
                            ))}
                        </Catcher>
                        <HorizBox spread wide>
                            <Byline
                                edited={toBool(comment.editedAt)}
                                originalLanguage={comment.originalLanguage}
                                profile={comment.author}
                                time={new Date(comment.createdAt).getTime()}
                                type='large'
                            />
                            <PostMenuButton comment={comment} />
                        </HorizBox>
                    </VertBox>

                    <VertBox gap={12}>
                        <Catcher>
                            {commentBelowBylineWidgets?.map((Widget, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Widget comment={comment} key={i} />
                            ))}
                        </Catcher>

                        <Catcher>
                            <CommentBody comment={comment} />
                        </Catcher>

                        <Catcher>
                            {commentMiddleWidgets?.map((Widget, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Widget comment={comment} key={i} />
                            ))}
                        </Catcher>
                    </VertBox>

                    <Catcher>
                        {commentBelowWidgets?.map((Widget, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Widget comment={comment} key={i} />
                        ))}
                    </Catcher>

                    {!editing && (
                        <Catcher>
                            <CommentActions comment={comment} depth={0} />
                        </Catcher>
                    )}
                </VertBox>
            </View>
            <MaybeCommentReply replyToComment={comment} />
            <CommentReplies parent={comment} replies={replies} />
        </View>
    );
}

export type ReplyCommentProps = {
    commentTree: CommentTreeNode;
    parent: Comment;
    depth?: number;
    isFinal?: boolean;
};

/** @public */
export function ReplyComment({ commentTree, parent, depth = 1, isFinal = false }: ReplyCommentProps) {
    const { comment, replies } = commentTree;
    const editing = useSessionData(['editComment', comment.commentKey]);
    const { replyAboveWidgets, replyBelowBylineWidgets, commentStylers } = useConfig<CommentConfig>();
    const datastore = useDatastore();
    const { colors } = useTheme();
    const style = getCombinedStyle({ comment, stylers: commentStylers, datastore, colors });
    const replyIndent = depth === 1 ? 12 : 0;

    return (
        <>
            <View id={comment.commentKey} style={[style, { paddingLeft: replyIndent, paddingVertical: 20 }]} testID={comment.commentKey}>
                <VertBox gap={20} wide>
                    <VertBox gap={8}>
                        <Catcher>
                            {replyAboveWidgets?.map((Widget, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Widget comment={comment} key={i} />
                            ))}
                        </Catcher>
                        <HorizBox spread wide>
                            <Byline
                                edited={toBool(comment.editedAt)}
                                originalLanguage={comment.originalLanguage}
                                profile={comment.author}
                                time={new Date(comment.createdAt).getTime()}
                                type={depth === 1 ? 'small' : 'tiny'}
                            />
                            <PostMenuButton comment={comment} />
                        </HorizBox>
                    </VertBox>

                    <Catcher>
                        {replyBelowBylineWidgets?.map((Widget, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Widget comment={comment} key={i} />
                        ))}
                    </Catcher>

                    <PadBox left={depth === 1 ? 40 : 32}>
                        <VertBox gap={20} wide>
                            <CommentBody comment={comment} parent={parent} />
                            {!editing && (
                                <Catcher>
                                    <CommentActions comment={comment} depth={depth} parent={parent} />
                                </Catcher>
                            )}
                        </VertBox>
                    </PadBox>
                </VertBox>
            </View>
            <PadBox left={replyIndent + (depth === 1 ? 40 : 32)}>
                <VertBox gap={20} wide>
                    <MaybeCommentReply replyToComment={comment} />
                    <CommentReplies depth={depth + 1} parent={comment} replies={replies} />
                    {!isFinal && <Separator />}
                </VertBox>
            </PadBox>
        </>
    );
}

type TranslatedCommentBodyRendererProps = {
    comment: Comment;
    color?: string;
    disableTruncation: boolean;
    onExpand: () => void;
};

function TranslatedCommentBody({ comment, color, disableTruncation, onExpand }: TranslatedCommentBodyRendererProps) {
    const displayText = useDisplayText(comment.text, comment.commentKey);
    return <ExpandableRichText color={color} disableTruncation={disableTruncation} onExpand={onExpand} text={displayText.trim()} />;
}

export type CommentBodyProps = { comment: Comment; parent?: Comment };

/** @public */
export function CommentBody({ comment, parent }: CommentBodyProps) {
    const editing = useSessionData(['editComment', comment.commentKey]);
    const [editedComment, setEditedComment] = useState<DraftComment | null>(null);
    const datastore = useDatastore();
    const {
        commentTopWidgets,
        commentBodyStylers,
        commentTruncationDisablers,
        commentYourReplyLabel = DEFAULT_YOUR_REPLY_LABEL,
    } = useConfig<CommentConfig>();
    const { colors } = useTheme();
    const commentBodyStyle = getCombinedStyle({ comment, stylers: commentBodyStylers, datastore, colors });
    const disableTruncation = commentTruncationDisablers?.some((disabler) => disabler({ datastore, comment }));

    function onEditingDone() {
        setEditedComment(null);
        datastore.setSessionData(['editComment', comment.commentKey], false);
    }

    function onCancel() {
        trackEvent(datastore, 'edit-cancel', { commentKey: comment.commentKey });
        setEditedComment(null);
        datastore.setSessionData(['editComment', comment.commentKey], false);
    }

    function onExpand() {
        trackEvent(datastore, 'comment-expanded', { commentKey: comment.commentKey });
    }

    if (editing) {
        const isReply = !!comment.parentKey;
        const editComponent = (
            <EditComment
                comment={editedComment ?? comment}
                isNewComment={false}
                onCancel={onCancel}
                onEditingDone={onEditingDone}
                parent={parent}
                setComment={setEditedComment}
            />
        );

        if (isReply) {
            return (
                <VertBox wide>
                    <UtilityText label={commentYourReplyLabel} type='small' weight='medium' />
                    {editComponent}
                </VertBox>
            );
        }

        return editComponent;
    }

    return (
        <View style={{ ...commentBodyStyle, width: '100%' }} testID='comment-body'>
            <VertBox gap={12}>
                {commentTopWidgets?.map((Widget, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Widget comment={comment} key={i} />
                ))}
                <TranslatedCommentBody
                    color={commentBodyStyle.color}
                    comment={comment}
                    disableTruncation={!!disableTruncation}
                    onExpand={onExpand}
                />
            </VertBox>
        </View>
    );
}

/** @public */
export function getCombinedStyle({
    comment,
    stylers,
    datastore,
    colors,
}: {
    comment: Comment | null;
    stylers: CommentStyler[];
    datastore: Datastore;
    colors: ColorTokens;
}): Record<string, any> {
    let style = {};
    if (!comment) return style;
    stylers?.forEach((styler) => {
        style = { ...style, ...styler({ datastore, comment, colors }) };
    });
    return style;
}

function makeDefaultComment(datastore: Datastore, overrides: Partial<DraftComment> = {}): DraftComment {
    const { commentDefaultFieldProviders } = datastore.getConfig<CommentConfig>() ?? {};
    const defaults = (commentDefaultFieldProviders ?? []).reduce<Record<string, JsonValue>>(
        (acc, creator) => ({ ...acc, ...(creator(datastore) as Record<string, JsonValue>) }),
        {},
    );
    return { text: '', properties: defaults, ...overrides };
}

type MaybeCommentReplyProps = {
    replyToComment: Comment;
};

function MaybeCommentReply({ replyToComment }: MaybeCommentReplyProps) {
    const replyEnabled = useSessionData(['replyToComment', replyToComment.commentKey]);
    const myProfile = useMyProfile();
    const datastore = useDatastore();
    const [comment, setComment] = useState<DraftComment>(() => makeDefaultComment(datastore, { parentKey: replyToComment.commentKey }));
    const { commentYourReplyLabel = DEFAULT_YOUR_REPLY_LABEL } = useConfig<CommentConfig>();
    useEffect(() => {
        if (replyEnabled) {
            trackEvent(datastore, 'reply-start', { replyToCommentKey: replyToComment.commentKey });
        }
    }, [replyEnabled, datastore, replyToComment.commentKey]);
    if (!replyEnabled) return null;

    function onEditingDone() {
        datastore.setSessionData(['replyToComment', replyToComment.commentKey], false);
        datastore.setSessionData(['showReplies', replyToComment.commentKey], true);
        setComment(makeDefaultComment(datastore, { parentKey: replyToComment.commentKey }));
    }

    function onCancel() {
        datastore.setSessionData(['replyToComment', replyToComment.commentKey], false);
        setComment(makeDefaultComment(datastore, { parentKey: replyToComment.commentKey }));
    }
    return (
        <PadBox vert={spacings.lg}>
            <VertBox gap={12} wide>
                <Byline profile={myProfile} showMetadata={false} type='small' />
                <VertBox gap={12} wide>
                    <UtilityText label={commentYourReplyLabel} type='small' weight='medium' />
                    <EditComment
                        comment={comment}
                        isNewComment
                        onCancel={onCancel}
                        onEditingDone={onEditingDone}
                        parent={replyToComment}
                        setComment={setComment}
                    />
                </VertBox>
            </VertBox>
        </PadBox>
    );
}

type EditCommentProps = {
    comment: DraftComment;
    parent?: Comment;
    isNewComment: boolean;
    big?: boolean;
    setComment: (comment: DraftComment) => void;
    topLevel?: boolean;
    onEditingDone: () => void;
    // Called after a new comment is posted, with the server-minted key, so the
    // composer records the identity and can transition the draft to the saved
    // comment.
    onPosted?: (commentKey: string) => void;
    onCancel?: () => void;
    onUserEdit?: () => void;
    onRejectionChange?: (rejection: CommentRejection | null) => void;
};

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function EditComment({
    comment,
    parent,
    isNewComment,
    big,
    setComment,
    topLevel,
    onEditingDone,
    onPosted,
    onCancel,
    onUserEdit,
    onRejectionChange,
}: EditCommentProps) {
    const { colors } = useTheme();
    const commentRef = useRef(comment);
    commentRef.current = comment;
    const personaKey = usePersonaKey();
    const datastore = useDatastore();
    const author = parent?.author ?? null;
    const [inProgress, setInProgress] = useState(false);
    const [shownModalElement, setShownModalElement] = useState<React.ReactElement | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeleteVoteModal, setShowDeleteVoteModal] = useState(false);
    const [commentErrorType, setCommentErrorType] = useState<false | 'blocking' | 'minor'>(false);
    const [rejection, setOwnRejection] = useState<CommentRejection | null>(null);
    const setRejection = useCallback(
        (next: CommentRejection | null) => {
            setOwnRejection(next);
            onRejectionChange?.(next);
        },
        [onRejectionChange],
    );
    const hasPerspectiveViolation = !!rejection?.perspectiveViolations?.length;

    // Local text buffer: prevents parent re-renders (e.g. from live
    // subscriptions) from resetting the DOM input value while the user is
    // actively typing.
    const [localText, setLocalText] = useState(originalTextOf(comment.text));
    const localTextRef = useRef(originalTextOf(comment.text));
    const isUserTypingRef = useRef(false);

    // Sync parent comment.text → localText only when user is NOT actively
    // typing. This accepts external updates (e.g. moderation setting rejected
    // text) while protecting against mid-typing resets from unrelated
    // re-renders.
    useEffect(() => {
        if (!isUserTypingRef.current) {
            setLocalText(originalTextOf(comment.text));
            localTextRef.current = originalTextOf(comment.text);
        }
    }, [comment.text]);

    const {
        commentReplyPlaceholder,
        commentInputPlaceholder,
        commentPostBlockers,
        commentEditTopWidgets,
        commentAllowEmpty,
        commentDeleteTriggers,
        commentNewCommentSecondaryAction,
    } = useConfig<CommentConfig>();
    const isInSidebar = useIsInSidebar();
    const rejectionInterceptModal = useRejectionInterceptModal();

    // Use localComment (with localText) so blockers and button state respond
    // immediately to user input, even before the parent state round-trip
    // completes.
    const localComment = useMemo(() => ({ ...comment, text: localText }), [comment, localText]);
    const isBlocked = commentPostBlockers?.some((blocker) => blocker(datastore, localComment));
    const canPost = (localText || commentAllowEmpty) && !isBlocked;
    const postDisabled = !canPost || inProgress || commentErrorType === 'blocking';

    const computedActionLabel = useModerationPostActionLabel({ inProgress, isNewComment, comment: localComment, rejection });
    const action =
        computedActionLabel ??
        (isNewComment
            ? inProgress
                ? t('comment.posting', 'Posting...')
                : t('comment.post_button', 'Post')
            : inProgress
              ? t('comment.updating', 'Updating...')
              : t('comment.update_button', 'Update'));
    const placeholder = comment.parentKey ? commentReplyPlaceholder : commentInputPlaceholder;

    async function showModalsAndFinish(modals: React.ComponentType<{ onClose: () => void }>[]) {
        function onClose() {
            fireAndForget(showModalsAndFinish(modals.slice(1)));
        }

        if (modals?.length) {
            const modalElement = React.createElement(modals[0], { onClose });
            setShownModalElement(modalElement);
        } else {
            setShownModalElement(null);
            onEditingDone();
        }
    }

    // "Post anyway": the comment was rejected as a minor violation and the
    // author chose to publish it as-is, promoting the server's rejection record
    // into a live comment.
    async function confirmPostAsync(_commentToPost: DraftComment, minorRejection: CommentRejection) {
        await confirmCommentAsync(datastore, { commentKey: minorRejection.commentKey });
        setRejection(null);
        setCommentErrorType(false);
        if (isNewComment) onPosted?.(minorRejection.commentKey);
        onEditingDone();
    }

    // "Post" / "Update": run the comment through the create/update API, which
    // moderates it. If the moderation mode wants to intercept the rejection
    // with its own modal it does so; otherwise the rejection becomes the
    // composer's `rejection` state and the moderation widgets render off it
    // (including the admin-bypass flow). An allowed post finishes (showing any
    // post-result modals first).
    async function postOrUpdateAsync(commentToPost: DraftComment) {
        const commentPostResult = await postCommentAsync({ datastore, comment: commentToPost });
        if (!commentPostResult.allowed) {
            const nextRejection = commentPostResult.rejection ?? null;
            commentPostResult.finalComment && setComment(commentPostResult.finalComment);
            if (nextRejection && showRejectionInterceptModal(commentToPost, nextRejection)) {
                return;
            }
            showRejection(nextRejection);
            return;
        }
        setRejection(null);
        setCommentErrorType(false);
        if (isNewComment && commentPostResult.finalComment?.commentKey) {
            onPosted?.(commentPostResult.finalComment.commentKey);
        }
        if (commentPostResult.modals.length > 0) {
            await showModalsAndFinish(commentPostResult.modals);
            return;
        }
        onEditingDone();
    }

    function showRejection(nextRejection: CommentRejection | null) {
        setRejection(nextRejection);
        // A failed post carrying no rejection leaves nothing to clear the
        // blocking state later, so it is dropped here.
        setCommentErrorType(nextRejection ? (nextRejection.minorViolation ? 'minor' : 'blocking') : false);
    }

    function showRejectionInterceptModal(commentToPost: DraftComment, pendingRejection: CommentRejection): boolean {
        if (!rejectionInterceptModal) return false;
        const modalElement = rejectionInterceptModal({
            comment: commentToPost,
            rejection: pendingRejection,
            isNewComment,
            cancel: () => {
                setShownModalElement(null);
                showRejection(pendingRejection);
            },
            post: () => {
                setShownModalElement(null);
                fireAndForget(runConfirmPostAsync(commentToPost, pendingRejection));
            },
        });
        if (!modalElement) return false;
        setShownModalElement(modalElement);
        return true;
    }

    // Confirming from the intercept modal runs outside `onPost`, so a failure
    // has no inline report to fall back on: it drops the author back into the
    // rejected state, where the post action re-runs `confirm` rather than
    // creating a second rejection for the same draft.
    async function runConfirmPostAsync(commentToPost: DraftComment, minorRejection: CommentRejection) {
        setInProgress(true);
        try {
            await confirmPostAsync(commentToPost, minorRejection);
        } catch (error) {
            captureException(error);
            showApiErrorToast({ datastore, error });
            showRejection(minorRejection);
        } finally {
            setInProgress(false);
        }
    }

    // Watched rather than wrapped around a setter: the perspective selector
    // renders outside this component and sets the draft directly.
    const previousPerspectivesRef = useRef(comment.perspectives);
    useEffect(() => {
        if (comment.perspectives === previousPerspectivesRef.current) return;
        previousPerspectivesRef.current = comment.perspectives;
        if (!hasPerspectiveViolation) return;
        setRejection(null);
        setCommentErrorType(false);
    }, [comment.perspectives, hasPerspectiveViolation, setRejection]);

    async function onPost() {
        isUserTypingRef.current = false;
        const commentToPost = { ...commentRef.current, text: localTextRef.current };
        const minorRejection = rejection?.minorViolation ? rejection : null;
        setInProgress(true);
        try {
            if (minorRejection) {
                // An untouched draft still carries the rejection the mode may
                // want to interpose on, so it gets the same modal a fresh
                // rejection would — confirming is what the modal's own action
                // does, not what pressing post again does.
                if (showRejectionInterceptModal(commentToPost, minorRejection)) return;
                await confirmPostAsync(commentToPost, minorRejection);
            } else {
                await postOrUpdateAsync(commentToPost);
            }
        } finally {
            setInProgress(false);
        }
    }

    // `deleteCommentAsync` optimistically blanks the comment in place as a
    // "deletion in progress" tombstone (`deletePending`, so its banner reads
    // "Deleting…" and its replies stay put), rolling that back via invalidation
    // if the request fails; the post-delete refetch then settles it into the
    // final tombstone or a removal — the server owns that outcome, so the
    // client doesn't guess. The API call stays fire-and-forget here; the UI
    // already reflects the pending deletion (or its rollback) without waiting.
    async function onDelete() {
        const commentKey = comment.commentKey;
        if (!commentKey) return;
        const pendingTombstone: Comment = {
            ...(comment as unknown as Comment),
            commentKey,
            authorKey: comment.authorKey ?? personaKey ?? '',
            createdAt: comment.createdAt ?? new Date(datastore.getTime()).toISOString(),
            text: '',
            deleted: true,
            properties: { ...comment.properties, deletePending: true },
        };
        if (commentDeleteTriggers?.length) {
            await Promise.all(commentDeleteTriggers.map((trigger) => trigger({ datastore, comment: pendingTombstone })));
        }
        fireAndForget(
            (async () => {
                try {
                    await deleteCommentAsync(datastore, pendingTombstone);
                } catch (error) {
                    captureException(error);
                    showApiErrorToast({ datastore, error });
                }
            })(),
        );
        onEditingDone();
    }

    const isMobile = getIsMobileWeb();
    const secondaryAction = isNewComment ? (commentNewCommentSecondaryAction?.({ datastore }) ?? null) : null;
    const showDelete = !isNewComment && !!comment.commentKey;
    /*
    The layout of the button changes as well in standalone (not sidebar) because
    the delete and cancel buttons are not in the navigation header anymore
    */
    const showInlineDelete = (showDelete || secondaryAction) && (!topLevel || !isInSidebar);
    const showButtonRow = isMobile || !topLevel || !isInSidebar;

    const FieldComponent = big ? TextArea : TextField;
    return (
        <VertBox gap={12} wide>
            {shownModalElement}
            {showDeleteModal && <DeleteCommentModal onClose={() => setShowDeleteModal(false)} onConfirm={onDelete} />}
            {showDeleteVoteModal && secondaryAction && (
                <DeleteVoteModal onClose={() => setShowDeleteVoteModal(false)} onConfirm={secondaryAction} />
            )}
            {topLevel && <NavigationHeaderAction disabled={postDisabled} label={action} onPress={onPost} />}
            {topLevel && (showDelete || secondaryAction) && (
                <NavigationHeaderAction
                    icon={<DeleteIcon />}
                    label={t('comment.delete_button', 'Delete')}
                    onPress={secondaryAction ? () => setShowDeleteVoteModal(true) : () => setShowDeleteModal(true)}
                    secondary
                    type='delete'
                />
            )}
            <EditWidgets
                comment={comment}
                loading={inProgress}
                onCancel={onCancel}
                onEditingDone={onEditingDone}
                rejection={rejection}
                setComment={setComment}
                widgets={commentEditTopWidgets}
            />

            <FieldComponent
                autoFocus={!isMobile}
                editable={!inProgress}
                error={commentErrorType === 'blocking'}
                multiline
                onChange={(text) => {
                    isUserTypingRef.current = true;
                    localTextRef.current = text;
                    setLocalText(text);
                    onUserEdit?.();
                    setComment({ ...commentRef.current, text });
                    if (rejection) {
                        setRejection(null);
                        setCommentErrorType(false);
                    }
                }}
                placeholderLabel={placeholder}
                placeholderParams={{ authorName: getFirstName(author?.name ?? '') }}
                testID='comment-edit'
                value={localText}
            />

            <CollapseBox style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <ModerationReportAndExplanation
                    comment={comment}
                    loading={inProgress}
                    onEditingDone={onEditingDone}
                    rejection={rejection}
                />
            </CollapseBox>

            {personaKey &&
                (showButtonRow ? (
                    <HorizBox center spread>
                        {showInlineDelete ? (
                            <CTAButton
                                icon={<DeleteIcon />}
                                onPress={secondaryAction ? () => setShowDeleteVoteModal(true) : () => setShowDeleteModal(true)}
                                testID='delete'
                                type='delete'
                            />
                        ) : (
                            <Pad />
                        )}
                        <HorizBox center right>
                            {onCancel && (
                                <PadBox right={16}>
                                    <TextButton
                                        color={colors.colorTextSecondary}
                                        label={t('comment.cancel_button', 'Cancel')}
                                        onPress={onCancel}
                                    />
                                </PadBox>
                            )}
                            <CTAButton disabled={postDisabled} label={action} onPress={onPost} testID='post-button' type='primary' />
                        </HorizBox>
                    </HorizBox>
                ) : (
                    <CTAButton disabled={postDisabled} label={action} onPress={onPost} testID='post-button' type='primary' wide />
                ))}
        </VertBox>
    );
}

type EditWidgetsProps = {
    comment: DraftComment;
    loading?: boolean;
    setComment: (comment: DraftComment) => void;
    onCancel?: () => void;
    onEditingDone?: () => void;
    rejection?: CommentRejection | null;
    widgets:
        | React.ComponentType<{
              comment: DraftComment;
              setComment: (comment: DraftComment) => void;
              loading?: boolean;
              onCancel?: () => void;
              onEditingDone?: () => void;
              rejection?: CommentRejection | null;
          }>[]
        | undefined;
};

function EditWidgets({ widgets, comment, setComment, onCancel, onEditingDone, loading, rejection }: EditWidgetsProps) {
    return (
        <CollapseBox style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {widgets?.map((Widget, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Catcher key={i}>
                    <Widget
                        comment={comment}
                        loading={loading}
                        onCancel={onCancel}
                        onEditingDone={onEditingDone}
                        rejection={rejection}
                        setComment={setComment}
                    />
                </Catcher>
            ))}
        </CollapseBox>
    );
}

type CommentRepliesProps = {
    replies: CommentTreeNode[];
    parent: Comment;
    depth?: number;
};

function countNestedReplies(nodes: CommentTreeNode[]): number {
    return nodes.reduce((total, node) => total + 1 + countNestedReplies(node.replies), 0);
}

function CommentReplies({ replies, parent, depth = 1 }: CommentRepliesProps) {
    const datastore = useDatastore();
    const expanded = useSessionData(['showReplies', parent.commentKey]);

    function setExpanded(value: boolean) {
        datastore.setSessionData(['showReplies', parent.commentKey], value);
        if (value) {
            trackEvent(datastore, 'showReplies', { commentKey: parent.commentKey });
        }
    }

    if (replies.length === 0) return null;

    const visibleBoosted = replies.filter((r) => r.boosted);
    const remainingReplies = replies.filter((r) => !r.boosted);
    const remainingCount = countNestedReplies(remainingReplies);

    return (
        <VertBox testID='comment-replies' wide>
            <Separator />

            {visibleBoosted.map((node, index) => (
                <ReplyComment
                    commentTree={node}
                    depth={depth}
                    isFinal={index === visibleBoosted.length - 1 && (remainingReplies.length === 0 || !expanded)}
                    key={node.comment.commentKey}
                    parent={parent}
                />
            ))}
            {expanded && remainingReplies.length > 0 && (
                <CatchList
                    getKey={(node) => node.comment.commentKey}
                    items={remainingReplies}
                    renderItem={(node, _index, isFinal) => (
                        <ReplyComment commentTree={node} depth={depth} isFinal={isFinal} parent={parent} />
                    )}
                />
            )}
            {remainingReplies.length > 0 && (
                <>
                    <PadBox vert={20}>
                        <ShowMoreRepliesButton expanded={expanded} onPress={() => setExpanded(!expanded)} remainingCount={remainingCount} />
                    </PadBox>
                </>
            )}
        </VertBox>
    );
}

function ShowMoreRepliesButton({ expanded, onPress, remainingCount }: { expanded: boolean; onPress: () => void; remainingCount: number }) {
    const text = useTranslation(
        expanded
            ? t('comment.show_less', 'Show less')
            : remainingCount === 1
              ? t('comment.show_more_reply_singular', 'Show {count} more reply')
              : t('comment.show_more_replies_plural', 'Show {count} more replies'),
        { count: remainingCount },
    );
    return <SubtleButton icon={expanded ? Subtract : Add} onPress={onPress} testID='toggle-replies' text={text} />;
}

type CommentActionsProps = {
    comment: Comment;
    parent?: Comment;
    depth: number;
};

function CommentActions({ comment, parent, depth }: CommentActionsProps) {
    const { commentActions, commentRightActions } = useConfig<CommentConfig>();

    // A deleted post is a tombstone (it shows the deleted-post banner instead
    // of a body); no edit/reply/react or reaction-summary affordances apply.
    if (comment.deleted) return null;

    if (!commentActions && !commentRightActions) return null;

    return (
        <HorizBox center spread wide>
            {commentActions && commentActions.length > 0 && (
                <HorizBox center gap={20}>
                    {commentActions.map((Action, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Action comment={comment} depth={depth} key={i} parent={parent} />
                    ))}
                </HorizBox>
            )}

            {commentRightActions && commentRightActions.length > 0 && (
                <HorizBox center gap={20}>
                    {commentRightActions.map((Action, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Action comment={comment} depth={depth} key={i} parent={parent} />
                    ))}
                </HorizBox>
            )}
        </HorizBox>
    );
}

export type ActionReplyExceptToSelfProps = { comment: Comment; parent?: Comment; depth: number };

export function ActionReplyExceptToSelf({ comment, parent, depth }: ActionReplyExceptToSelfProps) {
    const personaKey = usePersonaKey();
    if (comment.authorKey === personaKey) return null;
    if (depth === 1 && parent?.authorKey !== personaKey) return null;
    return <ActionReply comment={comment} depth={depth} />;
}

export type ActionReplyProps = { comment: Comment; depth: number };

export function ActionReply({ comment, depth }: ActionReplyProps) {
    const datastore = useDatastore();
    const readOnly = useIsReadOnly();
    const replyLabel = useTranslation(t('comment.reply_button', 'Reply'));

    function onReply() {
        const oldReply = datastore.getSessionData(['replyToComment', comment.commentKey]);
        datastore.setSessionData(['replyToComment', comment.commentKey], !oldReply);
    }

    if (depth > 1) return null;

    return <SubtleButton icon={Reply} isDisabled={readOnly} onPress={datastore.needsLogin(onReply, 'reply')} text={replyLabel} />;
}

export type ActionEditProps = { comment: Comment };

export function ActionEdit({ comment }: ActionEditProps) {
    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const readOnly = useIsReadOnly();
    const editLabel = useTranslation(t('comment.edit_button', 'Edit'));
    function onEdit() {
        if (!comment.parentKey) {
            trackEvent(datastore, 'edit-start-post', { commentKey: comment.commentKey });
            datastore.pushSubscreen('composer', { commentKey: comment.commentKey });
        } else {
            trackEvent(datastore, 'edit-start-reply', { commentKey: comment.commentKey });
            const old = datastore.getSessionData(['editComment', comment.commentKey]);
            datastore.setSessionData(['editComment', comment.commentKey], !old);
        }
    }

    if (comment.authorKey !== personaKey || readOnly) return null;
    return <SubtleButton icon={Edit} onPress={onEdit} text={editLabel} />;
}

/** @public */
export type ActionReportProps = { comment: Comment };

export type ComposerProps = { existingComment?: Comment | null; goBackAfterPost?: boolean; topLevel?: boolean };

export function Composer({ existingComment = null, goBackAfterPost = false, topLevel = false }: ComposerProps) {
    const [editedComment, setEditedComment] = useState<DraftComment | null>(null);
    const [postedKey, setPostedKey] = useState<string | null>(null);
    const personaKey = usePersonaKey() as string;
    const datastore = useDatastore();
    const { composerSubtitle, composerTopWidgets, composerAboveBylineWidgets } = useConfig<CommentConfig>();
    const [rejection, setRejection] = useState<CommentRejection | null>(null);
    const freshComment = useConstant(() =>
        makeDefaultComment(datastore, { authorKey: personaKey, createdAt: new Date(datastore.getTime()).toISOString() }),
    );
    const fetchedComment = useCommentByKey(existingComment?.commentKey ?? postedKey ?? null);
    const isNewComment = !existingComment && !postedKey;
    const comment: DraftComment = editedComment ?? fetchedComment ?? existingComment ?? freshComment;

    useEffect(() => {
        trackEvent(datastore, 'post-start', { commentKey: comment.commentKey });
    }, [comment.commentKey, datastore]);

    useEffect(() => {
        if (topLevel) {
            datastore.setSessionData('screenHasUnsavedChanges', originalTextOf(comment.text).length > 0);
        }
    }, [comment.text, datastore, topLevel]);

    const subtitle = composerSubtitle ? composerSubtitle({ datastore, comment }) : t('comment.public_comment_subtitle', 'Public Comment');

    function onFinish() {
        setEditedComment(null);

        if (topLevel) {
            datastore.setSessionData('screenHasUnsavedChanges', false);
        }

        if (goBackAfterPost) {
            datastore.goBack();
        }
    }

    function onEditingDone() {
        onFinish();
    }

    function onCancel() {
        trackEvent(datastore, isNewComment ? 'post-cancel' : 'edit-cancel', { commentKey: comment.commentKey });
        onFinish();
    }

    return (
        <VertBox gap={24}>
            <EditWidgets
                comment={comment}
                onCancel={goBackAfterPost ? onCancel : undefined}
                onEditingDone={onEditingDone}
                rejection={rejection}
                setComment={setEditedComment}
                widgets={[...(composerTopWidgets ?? []), ModerationAdminBypassBanner]}
            />
            <ComposerByline composerAboveBylineWidgets={composerAboveBylineWidgets} subtitleLabel={subtitle} />
            <EditComment
                big
                comment={comment}
                isNewComment={isNewComment}
                onCancel={goBackAfterPost ? onCancel : undefined}
                onEditingDone={onEditingDone}
                onPosted={setPostedKey}
                onRejectionChange={setRejection}
                setComment={setEditedComment}
                topLevel={topLevel}
            />
        </VertBox>
    );
}

function ComposerByline({
    composerAboveBylineWidgets: AboveBylineWidgets,
    subtitleLabel,
}: {
    composerAboveBylineWidgets?: React.ComponentType[];
    subtitleLabel: TLabel | undefined;
}) {
    const myProfile = useMyProfile();
    return (
        <VertBox gap={20}>
            <CollapseBox>
                {AboveBylineWidgets?.map((Widget, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Catcher key={i}>
                        <Widget />
                    </Catcher>
                ))}
            </CollapseBox>
            <Byline profile={myProfile} subtitleLabel={subtitleLabel} type='large' />
        </VertBox>
    );
}

export function CommentsInput() {
    const { commentInputPlaceholder, commentInputLoginAction } = useConfig<CommentConfig>();
    const datastore = useDatastore();
    const readonly = useIsReadOnly();
    const myProfile = useMyProfile();
    const placeholderText = useTranslation(commentInputPlaceholder);
    if (readonly) return null;
    return (
        <ComposerButton
            onPress={datastore.needsLogin(() => datastore.pushSubscreen('composer', {}), commentInputLoginAction)}
            placeholderText={placeholderText}
            profile={myProfile}
            testID='comment-input'
        />
    );
}

export function CommentPageTopWidgets() {
    const { pageTopWidgets } = useConfig<CommentConfig>();
    const { tree } = useCommentTree();
    const comments = tree.map((node) => node.comment);
    return (
        <View style={{ gap: 32 }}>
            {pageTopWidgets?.map((Widget, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Catcher key={i}>
                    <Widget comments={comments} />
                </Catcher>
            ))}
        </View>
    );
}

/** @public */
export function NoCommentsHelp() {
    const s = useNoCommentsHelpStyles();
    const { noCommentsMessage, noCommentsTitle, betweenCommentsWidgets } = useConfig<CommentConfig>();
    return (
        <>
            <PadBox vert={spacings.xl2}>
                <View style={s.helpBox}>
                    <Image source={{ uri: makeAssetUrl('images/bubbles.png') }} style={{ width: 58, height: 58 }} />
                    <View style={s.right}>
                        <Heading label={noCommentsTitle} />
                        <Pad size={spacings.xxs} />
                        <RichText label={noCommentsMessage} />
                    </View>
                </View>
            </PadBox>
            {betweenCommentsWidgets ? (
                <Catcher>
                    <BetweenCommentsWidgets betweenCommentsWidgets={betweenCommentsWidgets} />
                </Catcher>
            ) : null}
        </>
    );
}

const useNoCommentsHelpStyles = createThemedStyles((tokens) => ({
    helpBox: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        paddingVertical: 24,
        paddingHorizontal: 16,
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: 8,
    },
    right: {
        marginLeft: 12,
        flex: 1,
        justifyContent: 'center',
    },
}));

type BasicCommentsTopWidgetsProps = {
    pageTopLeftWidgets?: React.ComponentType<{ comments: Comment[] }>[] | undefined;
    pageTopRightWidgets?: React.ComponentType<{ comments: Comment[] }>[] | undefined;
    comments: Comment[];
};

function BasicCommentsTopWidgets({ pageTopLeftWidgets, pageTopRightWidgets, comments }: BasicCommentsTopWidgetsProps) {
    const s = BasicCommentsStyles;
    const hasWidgets = (pageTopLeftWidgets && pageTopLeftWidgets.length > 0) || (pageTopRightWidgets && pageTopRightWidgets.length > 0);
    if (!hasWidgets) {
        return null;
    }
    return (
        <View style={s.container}>
            <View style={s.leftContainer}>
                {pageTopLeftWidgets?.map((Widget, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Catcher key={i}>
                        <Widget comments={comments} />
                    </Catcher>
                ))}
            </View>
            <View style={s.rightContainer}>
                {pageTopRightWidgets?.map((Widget, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Catcher key={i}>
                        <Widget comments={comments} />
                    </Catcher>
                ))}
            </View>
        </View>
    );
}

const renderSeparator = () => <Separator />;

type BasicCommentsBottomWidgetsProps = {
    pageBottomWidgets?: React.ComponentType<{ comments: Comment[] }>[] | undefined;
    pageHiddenWidgets?: React.ComponentType<{ comments: Comment[] }>[] | undefined;
    comments: Comment[];
};

function BasicCommentsBottomWidgets({ pageBottomWidgets, pageHiddenWidgets, comments }: BasicCommentsBottomWidgetsProps) {
    return (
        <>
            <View>
                {pageBottomWidgets?.map((Widget, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Catcher key={i}>
                        <Widget comments={comments} />
                    </Catcher>
                ))}
            </View>
            {pageHiddenWidgets?.map((Widget, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Catcher key={i}>
                    <Widget comments={comments} />
                </Catcher>
            ))}
        </>
    );
}

export type CommentTreeProps = { tree: CommentTreeNode[]; isPending?: boolean };

/** @public */
export function CommentTree({ tree, isPending = false }: CommentTreeProps) {
    const { betweenCommentsWidgets, pageShowEmptyHelp } = useConfig<CommentConfig>();
    const userLoaded = useUserLoaded();
    const isDesktop = useIsDesktopBreakpoint();

    if (tree.length === 0) {
        if (!userLoaded || isPending) {
            return (
                <View testID='comment-tree-loading'>
                    {Array.from({ length: 3 }).map((_, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <CommentSkeleton key={i} variant={isDesktop ? 'desktop' : 'mobile'} />
                    ))}
                </View>
            );
        }
        return pageShowEmptyHelp ? <NoCommentsHelp /> : null;
    }

    return (
        <CatchList
            getKey={(node) => node.comment.commentKey}
            items={tree}
            renderItem={(node, index) => (
                <>
                    <Comment commentTree={node} />
                    {(index === 1 || tree.length === 1) && (
                        <BetweenCommentsWidgets betweenCommentsWidgets={betweenCommentsWidgets} withTopSeparator />
                    )}
                </>
            )}
            separator={renderSeparator}
            showLastSeparator
        />
    );
}

/** @public */
export function BaseComments() {
    const { tree, isPending } = useCommentTree();
    return <CommentTree isPending={isPending} tree={tree} />;
}

export type BasicCommentsProps = { showInput?: boolean; canPost?: boolean };

export function BasicComments({ showInput = true, canPost = true }: BasicCommentsProps) {
    const { pageTopLeftWidgets, pageTopRightWidgets, pageBottomWidgets, pageHiddenWidgets } = useConfig<CommentConfig>();
    const { tree } = useCommentTree();
    const comments = tree.map((node) => node.comment);

    return (
        <View style={{ paddingTop: spacings.xxs }}>
            <BasicCommentsTopWidgets
                comments={comments}
                pageTopLeftWidgets={pageTopLeftWidgets}
                pageTopRightWidgets={pageTopRightWidgets}
            />
            <View>
                <BaseComments />
                <BasicCommentsBottomWidgets
                    comments={comments}
                    pageBottomWidgets={pageBottomWidgets}
                    pageHiddenWidgets={pageHiddenWidgets}
                />
            </View>
        </View>
    );
}

const BasicCommentsStyles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    leftContainer: {
        flex: 1,
        alignItems: 'flex-start',
    },
    rightContainer: {
        flex: 1,
        alignItems: 'flex-end',
    },
});

const BetweenCommentsWidgets = ({
    betweenCommentsWidgets,
    withTopSeparator,
}: {
    betweenCommentsWidgets?: React.ComponentType<{ withTopSeparator?: boolean }>[] | undefined;
    withTopSeparator?: boolean;
}) =>
    betweenCommentsWidgets?.map((Widget, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Widget key={i} withTopSeparator={withTopSeparator} />
    )) ?? null;

export type ComposerAndCommentsProps = { commentKey: string };

export function ComposerAndComments({ commentKey }: ComposerAndCommentsProps) {
    const existingComment = useCommentByKey(commentKey);
    return (
        <View>
            <Composer existingComment={existingComment} goBackAfterPost />
            <BasicComments />
        </View>
    );
}

export type PostMenuButtonProps = { comment: Comment };

/** @public */
export function PostMenuButton({ comment }: PostMenuButtonProps) {
    const { postMenuActions } = useConfig<CommentConfig>();
    const datastore = useDatastore();
    const { tree } = useCommentTree();
    const readOnly = useIsReadOnly();
    const actions = postMenuActions?.flatMap((action) => action({ datastore, comment, tree, readOnly })) ?? [];
    if (actions.length === 0) return null;
    return <ActionsMenu actionArgs={{ comment }} actions={actions} testID={'menu-' + comment.commentKey} />;
}

function pickApiProperties(comment: DraftComment): Record<string, JsonValue> {
    const source = comment.properties ?? {};
    const props: Record<string, JsonValue> = {};
    for (const key of commentPropertyKeys) {
        const value = source[key];
        if (value !== undefined) props[key] = value;
    }
    return props;
}

type CommentApiResult = Awaited<ReturnType<ReturnType<Datastore['getApiClient']>['comments']['create']>>;
type ApiRejection = Extract<CommentApiResult, { outcome: 'rejected' }>['rejection'];

/**
 * Transient state describing why the most recent `comments.create` / `update`
 * call was rejected by moderation. Held by the composer so the user can act on
 * it (post-anyway, appeal). Does not live in the comment table — the server
 * didn't write a comment, only a rejection record keyed by the intended
 * `commentKey`.
 */
export type CommentRejection = {
    commentKey: string;
    violations: ModerationRule[];
    minorViolation: boolean;
    moderationSystemDown: boolean;
    /**
     * Guidelines tripped by the comment's write-in perspective rather than its
     * body. The Representing Perspectives feature renders these beside the
     * field they came from, and their presence means the author cannot post
     * past the rejection — there is no moderator review for a perspective, so
     * they have to change it.
     */
    perspectiveViolations?: ModerationRule[];
};

function rejectionFromResult(rejection: ApiRejection): CommentRejection {
    return {
        commentKey: rejection.rejectionKey,
        violations: rejection.violations,
        minorViolation: rejection.minorViolation,
        moderationSystemDown: rejection.systemDown,
        ...(rejection.perspectiveViolations ? { perspectiveViolations: rejection.perspectiveViolations } : {}),
    };
}

/**
 * Logs `post-finish` / `reply-finish` / `edit-finish` once the server has
 * answered. The analytics dashboard counts comments by `commentKey`, which
 * only exists once the server has minted it (or the rejection key).
 */
function trackFinishEvent({
    datastore,
    comment,
    commentKey,
    text,
}: {
    datastore: Datastore;
    comment: DraftComment;
    commentKey: string;
    text: string;
}) {
    if (comment.commentKey) {
        trackEvent(datastore, 'edit-finish', { commentKey, text, comment });
    } else if (comment.parentKey) {
        trackEvent(datastore, 'reply-finish', { commentKey, replyToCommentKey: comment.parentKey, text, comment });
    } else {
        trackEvent(datastore, 'post-finish', { commentKey, text, comment });
    }
}

/**
 * Writes the comment via the oRPC API — the only path that persists changes.
 * `postOrUpdateCommentAsync` (`data/comment`) owns the create-vs-update
 * decision, the optimistic tree patch for an edit, seeding `comments.get`'s
 * cache with the response, and invalidating every comment-derived read on
 * success; this function is left with the UI-facing concerns: analytics, the
 * error toast, and post-triggers. The API returns a `posted` result (the
 * stored comment) or a `rejected` result (the server kept a rejection
 * record, not a comment); we surface the latter as a typed `rejection` so
 * the composer can drive the post-anyway / appeal UI off it.
 */
async function postCommentViaApiAsync({ datastore, comment }: { datastore: Datastore; comment: DraftComment }): Promise<PostedOrRejected> {
    const { commentPostTriggers } = datastore.getConfig<CommentConfig>() ?? {};
    const text = originalTextOf(comment.text);

    let result: CommentApiResult;
    try {
        result = await postOrUpdateCommentAsync(datastore, {
            commentKey: comment.commentKey,
            text,
            parentKey: comment.parentKey,
            properties: pickApiProperties(comment),
            perspectives: comment.perspectives ? perspectiveTagsToInput(comment.perspectives) : undefined,
        });
    } catch (error) {
        // Reported as a failed post rather than rethrown: nothing above here
        // catches, so throwing surfaces as an unhandled rejection on top of
        // the toast the reader has already been shown.
        showApiErrorToast({ datastore, error });
        return { finalComment: comment, rejection: null, failed: true };
    }

    const commentKey = result.outcome === 'rejected' ? result.rejection.rejectionKey : result.comment.commentKey;
    trackFinishEvent({ datastore, comment, commentKey, text });

    if (result.outcome === 'rejected') {
        return { finalComment: comment, rejection: rejectionFromResult(result.rejection) };
    }

    const response = result.comment;
    const finalComment: Comment = { ...comment, ...response };
    fireAndForget(scoreCommentAsync(datastore, { commentKey: finalComment.commentKey }));
    for (const trigger of commentPostTriggers ?? []) {
        fireAndForget(trigger({ datastore, comment: finalComment }));
    }
    return { finalComment, rejection: null };
}

type PostedOrRejected =
    | { finalComment: Comment; rejection: null; failed?: false }
    | { finalComment: DraftComment; rejection: CommentRejection; failed?: false }
    | { finalComment: DraftComment; rejection: null; failed: true };

export type PostCommentResult = {
    allowed: boolean;
    error: boolean;
    modals: React.ComponentType<{ onClose: () => void }>[];
    finalComment?: DraftComment;
    /** Why the save was refused, tagged by which flow refused it. */
    rejection?: CommentRejection;
};

export async function postCommentAsync({
    datastore,
    comment,
}: {
    datastore: Datastore;
    comment: DraftComment;
}): Promise<PostCommentResult> {
    trackEvent(datastore, 'post-button-click', { commentKey: comment.commentKey, text: originalTextOf(comment.text) });

    const result = await postCommentViaApiAsync({ datastore, comment });
    if (result.failed) {
        return { allowed: false, error: true, modals: [], finalComment: result.finalComment };
    }
    if (result.rejection) {
        return { allowed: false, error: false, modals: [], finalComment: result.finalComment, rejection: result.rejection };
    }
    const finalComment = result.finalComment;

    // A post the server queued for a human is the one outcome the author has
    // to be told about after a successful save: nothing of theirs appeared.
    const modals = finalComment.moderationState?.inReview ? [HumanModerationModal] : [];
    return { allowed: true, error: false, modals, finalComment };
}
